import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { DpDocumentType, listDocumentTypesApi } from "../api/documentsApi";
import {
  createProcessTemplateApi,
  DpProcessKind,
  DpProcessTemplate,
  DpStepResponsible,
  DpTemplateStep,
  listProcessTemplatesApi,
  PROCESS_KIND_LABELS,
  seedProcessDefaultsApi,
  STEP_RESPONSIBLE_LABELS,
  SYSTEM_ACTION_BY_KIND,
  SYSTEM_ACTION_LABELS,
  updateProcessTemplateApi,
} from "../api/processesApi";

type StepRow = {
  title: string;
  description: string;
  responsible: DpStepResponsible;
  documentTypeId: string;
  dueOffsetDays: string;
  hasAction: boolean;
};

const emptyStep = (): StepRow => ({
  title: "",
  description: "",
  responsible: "DP",
  documentTypeId: "",
  dueOffsetDays: "",
  hasAction: false,
});

const toRow = (step: DpTemplateStep): StepRow => ({
  title: step.title,
  description: step.description ?? "",
  responsible: step.responsible,
  documentTypeId: step.documentTypeId ?? "",
  dueOffsetDays: step.dueOffsetDays === null ? "" : String(step.dueOffsetDays),
  hasAction: Boolean(step.systemAction),
});

/**
 * Aba "Checklists" em Cadastros. Editar um modelo não muda processos em
 * andamento — cada processo guarda a cópia das etapas de quando foi aberto.
 */
export default function ProcessTemplatesSettings({ token }: { token: string }) {
  const [templates, setTemplates] = useState<DpProcessTemplate[]>([]);
  const [documentTypes, setDocumentTypes] = useState<DpDocumentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editing, setEditing] = useState<{ id?: string; kind: DpProcessKind } | null>(null);
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [steps, setSteps] = useState<StepRow[]>([emptyStep()]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d] = await Promise.all([listProcessTemplatesApi(token, undefined, true), listDocumentTypesApi(token)]);
      setTemplates(t);
      setDocumentTypes(d);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao carregar os checklists.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (kind: DpProcessKind, template?: DpProcessTemplate) => {
    setEditing({ id: template?.id, kind });
    setName(template?.name ?? "");
    setIsDefault(template?.isDefault ?? false);
    setIsActive(template?.isActive ?? true);
    setSteps(template ? template.steps.map(toRow) : [emptyStep()]);
  };

  const updateStep = (index: number, patch: Partial<StepRow>) =>
    setSteps((rows) =>
      rows.map((row, i) => {
        if (i === index) return { ...row, ...patch };
        // A ação de sistema só pode estar em uma etapa.
        if (patch.hasAction) return { ...row, hasAction: false };
        return row;
      })
    );

  const move = (index: number, delta: number) =>
    setSteps((rows) => {
      const target = index + delta;
      if (target < 0 || target >= rows.length) return rows;
      const next = [...rows];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const save = async () => {
    if (!editing) return;
    if (name.trim().length < 2) return toast.error("Informe o nome do checklist.");
    if (steps.some((s) => s.title.trim().length < 2)) return toast.error("Toda etapa precisa de um título.");
    if (editing.kind === "ADMISSAO" && isActive && !steps.some((s) => s.hasAction)) {
      return toast.error('O checklist de admissão precisa da etapa "Criar acesso ao app".');
    }

    const action = SYSTEM_ACTION_BY_KIND[editing.kind];
    const payload = {
      name: name.trim(),
      isDefault,
      isActive,
      steps: steps.map((s) => ({
        title: s.title.trim(),
        description: s.description.trim() || null,
        responsible: s.responsible,
        documentTypeId: s.documentTypeId || null,
        dueOffsetDays: s.dueOffsetDays === "" ? null : Number(s.dueOffsetDays),
        systemAction: s.hasAction ? action : null,
      })),
    };

    setSaving(true);
    try {
      if (editing.id) await updateProcessTemplateApi(token, editing.id, payload);
      else await createProcessTemplateApi(token, { ...payload, kind: editing.kind });
      toast.success("Checklist salvo.");
      setEditing(null);
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao salvar o checklist.");
    } finally {
      setSaving(false);
    }
  };

  const seed = async () => {
    setSaving(true);
    try {
      const { created } = await seedProcessDefaultsApi(token);
      toast.success(created.length ? `Criados: ${created.join(", ")}.` : "Os padrões já existiam — nada foi criado.");
      load();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao criar os padrões.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="dpControls">
        <button className="dpBtn dpBtn--primary" onClick={() => open("ADMISSAO")}>
          Novo checklist de admissão
        </button>
        <button className="dpBtn dpBtn--primary" onClick={() => open("DESLIGAMENTO")}>
          Novo checklist de desligamento
        </button>
        <button className="dpBtn dpBtn--secondary" onClick={seed} disabled={saving}>
          Criar padrões
        </button>
      </div>
      <p className="dpFormHint" style={{ marginBottom: 12 }}>
        O checklist marcado como padrão é o usado ao iniciar uma admissão ou um desligamento. Mudar o
        modelo não altera processos já abertos.
      </p>

      {loading ? <div className="dpEmpty">Carregando...</div> : null}
      {!loading && templates.length === 0 ? (
        <div className="dpEmpty">Nenhum checklist cadastrado. Use "Criar padrões" para começar.</div>
      ) : null}

      {templates.map((t) => (
        <section key={t.id} className="dpSection">
          <div className="dpHeader" style={{ marginBottom: 12 }}>
            <h3 className="dpSection__title" style={{ margin: 0 }}>
              {PROCESS_KIND_LABELS[t.kind]} · {t.name}{" "}
              {t.isDefault ? <span className="dpBadge dpBadge--blue">Padrão</span> : null}{" "}
              {!t.isActive ? <span className="dpBadge dpBadge--gray">Inativo</span> : null}
            </h3>
            <button className="dpBtn dpBtn--secondary" onClick={() => open(t.kind, t)}>
              Editar
            </button>
          </div>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {t.steps.map((s) => (
              <li key={s.id ?? s.title} style={{ marginBottom: 4 }}>
                {s.title}
                <span className="dpFormHint">
                  {" "}
                  · {STEP_RESPONSIBLE_LABELS[s.responsible]}
                  {s.dueOffsetDays !== null ? ` · prazo D${s.dueOffsetDays >= 0 ? "+" : ""}${s.dueOffsetDays}` : ""}
                  {s.documentType ? ` · exige ${s.documentType.name}` : ""}
                  {s.systemAction ? ` · ${SYSTEM_ACTION_LABELS[s.systemAction]}` : ""}
                </span>
              </li>
            ))}
          </ol>
        </section>
      ))}

      {editing ? (
        <div className="dpModalOverlay">
          <div className="dpModal dpModal--wide">
            <header className="dpModal__header">
              <h2 className="dpModal__title">
                {editing.id ? "Editar" : "Novo"} checklist de {PROCESS_KIND_LABELS[editing.kind].toLowerCase()}
              </h2>
              <button className="dpModal__close" onClick={() => setEditing(null)} disabled={saving}>
                &times;
              </button>
            </header>
            <div className="dpModal__body">
              <div className="dpFormGroup">
                <label htmlFor="tplName">Nome *</label>
                <input
                  id="tplName"
                  className="dpInput"
                  maxLength={120}
                  value={name}
                  disabled={saving}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <label className="dpCheckbox">
                <input type="checkbox" checked={isDefault} disabled={saving} onChange={(e) => setIsDefault(e.target.checked)} />
                Checklist padrão
              </label>
              <label className="dpCheckbox">
                <input type="checkbox" checked={isActive} disabled={saving} onChange={(e) => setIsActive(e.target.checked)} />
                Ativo
              </label>

              <p className="dpFormHint">
                Prazo em dias a partir da {editing.kind === "ADMISSAO" ? "admissão" : "data do último dia trabalhado"} (negativo = antes).
                A etapa marcada com "{SYSTEM_ACTION_LABELS[SYSTEM_ACTION_BY_KIND[editing.kind]]}" executa a ação no sistema.
              </p>

              <div className="dpDependentRows">
                {steps.map((step, index) => (
                  <div key={index} className="dpDependentRow">
                    <div className="dpDependentRow__header">
                      <span className="dpDependentRow__title">Etapa {index + 1}</span>
                      <div className="dpTable__actions">
                        <button type="button" className="dpBtn dpBtn--secondary dpBtn--small" disabled={saving || index === 0} onClick={() => move(index, -1)} aria-label="Subir">
                          ↑
                        </button>
                        <button
                          type="button"
                          className="dpBtn dpBtn--secondary dpBtn--small"
                          disabled={saving || index === steps.length - 1}
                          onClick={() => move(index, 1)}
                          aria-label="Descer"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          className="dpBtn dpBtn--danger dpBtn--small"
                          disabled={saving || steps.length === 1}
                          onClick={() => setSteps((rows) => rows.filter((_, i) => i !== index))}
                        >
                          Remover
                        </button>
                      </div>
                    </div>
                    <div className="dpFormGrid">
                      <div className="dpFormGroup">
                        <label>Título *</label>
                        <input
                          className="dpInput"
                          maxLength={200}
                          value={step.title}
                          disabled={saving}
                          onChange={(e) => updateStep(index, { title: e.target.value })}
                        />
                      </div>
                      <div className="dpFormGroup">
                        <label>Responsável</label>
                        <select
                          className="dpSelect"
                          value={step.responsible}
                          disabled={saving}
                          onChange={(e) => updateStep(index, { responsible: e.target.value as DpStepResponsible })}
                        >
                          {(Object.keys(STEP_RESPONSIBLE_LABELS) as DpStepResponsible[]).map((r) => (
                            <option key={r} value={r}>
                              {STEP_RESPONSIBLE_LABELS[r]}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="dpFormGroup">
                        <label>Exige documento</label>
                        <select
                          className="dpSelect"
                          value={step.documentTypeId}
                          disabled={saving}
                          onChange={(e) => updateStep(index, { documentTypeId: e.target.value })}
                        >
                          <option value="">Nenhum</option>
                          {documentTypes.map((d) => (
                            <option key={d.id} value={d.id}>
                              {d.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="dpFormGroup">
                        <label>Prazo (dias)</label>
                        <input
                          type="number"
                          className="dpInput"
                          min={-90}
                          max={365}
                          value={step.dueOffsetDays}
                          disabled={saving}
                          onChange={(e) => updateStep(index, { dueOffsetDays: e.target.value })}
                        />
                      </div>
                    </div>
                    <label className="dpCheckbox">
                      <input
                        type="checkbox"
                        checked={step.hasAction}
                        disabled={saving}
                        onChange={(e) => updateStep(index, { hasAction: e.target.checked })}
                      />
                      {SYSTEM_ACTION_LABELS[SYSTEM_ACTION_BY_KIND[editing.kind]]}
                    </label>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="dpBtn dpBtn--secondary"
                style={{ marginTop: 12 }}
                disabled={saving}
                onClick={() => setSteps((rows) => [...rows, emptyStep()])}
              >
                Adicionar etapa
              </button>
            </div>
            <footer className="dpModal__footer">
              <button className="dpBtn dpBtn--secondary" onClick={() => setEditing(null)} disabled={saving}>
                Cancelar
              </button>
              <button className="dpBtn dpBtn--primary" onClick={save} disabled={saving}>
                Salvar
              </button>
            </footer>
          </div>
        </div>
      ) : null}
    </>
  );
}
